<template>
  <div class="mb-air">
    <div class="mb-air__scenes" role="group" aria-label="Сцена превью">
      <button v-for="s in scenes"
              :key="s.key"
              type="button"
              :class="['mb-air__scene', {'is-preview': preview.scene === s.key, 'is-program': program.scene === s.key}]"
              :aria-pressed="preview.scene === s.key"
              @click="setPreviewScene(s.key)">
        <span class="mb-air__key">{{ s.hotkey }}</span>
        <span class="mb-air__scene-text">
          <b>{{ s.title }}</b>
          <span class="mb-muted mb-small">{{ s.hint }}</span>
        </span>
        <span v-if="program.scene === s.key" class="mb-badge mb-badge--danger">в эфире</span>
        <span v-else-if="suggested === s.key" class="mb-badge mb-badge--success">по данным</span>
      </button>
    </div>

    <div v-if="preview.scene === 'post'" class="mb-air__games">
      <span class="mb-muted mb-small">Игра для итогов:</span>
      <span v-if="!postGames.length" class="mb-muted mb-small">ни одна игра серии ещё не закончилась</span>
      <button v-for="g in postGames"
              :key="g.id"
              type="button"
              :class="['mb-chip', {'is-active': preview.postId === g.id}]"
              @click="setPreviewPost(g.id)">
        {{ gameLabel(g) }}
      </button>
    </div>

    <div class="mb-air__monitors">
      <section class="mb-air__monitor">
        <header class="mb-air__monitor-head">
          <b>Превью</b>
          <span class="mb-muted mb-small">{{ sceneTitle(preview.scene) }} · видно только здесь</span>
        </header>
        <MbOverlayStage>
          <MbOverlay admin
                     :scene="preview.scene"
                     :battle="previewBattle || battle"
                     :post-battle="postBattles[preview.postId] || null"
                     :events="events"
                     :series="series"
                     :title="previewTitle"
                     :casters="previewCasters"
                     :now="now"/>
        </MbOverlayStage>
      </section>

      <aside class="mb-air__side">
        <section class="mb-air__monitor mb-air__monitor--program">
          <header class="mb-air__monitor-head">
            <b><span class="mb-dot mb-dot--error"></span> Эфир</b>
            <span class="mb-muted mb-small">{{ programLabel }}</span>
          </header>
          <MbOverlayStage>
            <MbOverlay :scene="program.scene"
                       :battle="programBattle"
                       :post-battle="programPostBattle"
                       :events="events"
                       :series="series"
                       :title="programTitle"
                       :casters="programCasters"
                       :now="now"/>
          </MbOverlayStage>
        </section>

        <button type="button"
                class="mb-btn mb-btn--danger mb-btn--lg mb-air__take"
                :disabled="!canTake"
                @click="$emit('take')">
          В эфир <kbd>Enter</kbd>
        </button>
        <div v-if="obsUrl" class="mb-air__obs">
          <span class="mb-muted mb-small">Ссылка для OBS (Browser Source 1920×1080)</span>
          <div class="mb-air__obs-row">
            <code class="mb-mono mb-ellipsis" :title="obsUrl">{{ obsUrl }}</code>
            <button type="button" class="mb-btn mb-btn--ghost mb-btn--sm" @click="copyObsUrl">Копировать</button>
          </div>
        </div>
        <p class="mb-muted mb-small mb-air__note">
          OBS показывает только то, что выведено кнопкой «В эфир»: сам по состоянию игры сцену не меняет
          и на следующую игру серии не переходит. Ссылка одна на трансляцию — при смене матча её менять не нужно,
          входить в админку в OBS не надо. Названия, лого, ники и фото правятся во вкладке «Оформление».
        </p>
      </aside>
    </div>
  </div>
</template>

<script>
import MbOverlay from '@/components/MlbbBroadcast/overlay/MbOverlay.vue'
import MbOverlayStage from '@/components/MlbbBroadcast/overlay/MbOverlayStage.vue'
import {campByNumber, campShortName, formatGameTime} from '@/common/mlbbBroadcast'
import {
  endedGames,
  gameNumber,
  isTypingTarget,
  SCENES,
  sceneByHotkey,
  sceneTitle,
  suggestedScene,
} from '@/common/mlbbOverlay'

export default {
  name: 'MbAirPanel',
  components: {MbOverlay, MbOverlayStage},
  props: {
    // сырой источник: подсказка сцены и список игр для итогов
    battle: {type: Object, default: null},
    // источник с правками трансляции — так его нарисует OBS после «В эфир»
    previewBattle: {type: Object, default: null},
    // что сейчас в OBS: данные с бэка уже с правками
    programBattle: {type: Object, default: null},
    programPostBattle: {type: Object, default: null},
    previewTitle: {type: String, default: ''},
    previewCasters: {type: Array, default: () => []},
    programTitle: {type: String, default: ''},
    programCasters: {type: Array, default: () => []},
    events: {type: Array, default: () => []},
    series: {type: Array, default: () => []},
    // завершённые игры для «Итогов» по id (текущая игра и догруженные игры серии)
    postBattles: {type: Object, default: () => ({})},
    preview: {type: Object, required: true},
    program: {type: Object, required: true},
    // трансляция, в которую уходит «В эфир» и которую слушает OBS
    broadcastId: {type: Number, default: null},
    // «В эфир» отправляется на бэк
    taking: {type: Boolean, default: false},
    // горячие клавиши слушаем, только пока вкладка открыта
    active: {type: Boolean, default: false},
    now: {type: Number, default: () => Date.now()},
  },
  data() {
    return {scenes: SCENES}
  },
  computed: {
    suggested() {
      return suggestedScene(this.battle)
    },
    postGames() {
      return endedGames(this.series, this.battle)
    },
    canTake() {
      if (this.taking || !this.broadcastId) return false
      if (this.preview.scene === 'post' && !this.preview.postId) return false
      return this.preview.scene !== this.program.scene || this.preview.postId !== this.program.postId
    },
    obsUrl() {
      if (!this.broadcastId) return ''
      return `${window.location.origin}/mlbb-broadcast/obs?broadcast=${this.broadcastId}`
    },
    programLabel() {
      const title = sceneTitle(this.program.scene)
      if (this.program.scene !== 'post') return title
      const game = this.postGames.find((g) => g.id === this.program.postId)
      return game ? `${title} · ${this.gameLabel(game)}` : title
    },
  },
  mounted() {
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    sceneTitle,

    gameLabel(g) {
      const n = gameNumber(this.series, g.id)
      const winner = g.win_camp ? campShortName(campByNumber(g, g.win_camp)) : null
      return `${n ? `Игра ${n}` : 'Игра'}${winner ? ` · ${winner}` : ''} · ${formatGameTime(g.game_time)}`
    },

    copyObsUrl() {
      const done = () => this.$bvToast.toast('Ссылка для OBS скопирована', {
        title: 'Готово', variant: 'success', solid: true,
      })
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.obsUrl).then(done).catch(() => {})
      }
    },

    setPreviewScene(scene) {
      const patch = {scene}
      // для итогов сразу берём последнюю завершённую игру
      if (scene === 'post' && !this.postGames.some((g) => g.id === this.preview.postId)) {
        patch.postId = this.postGames.length ? this.postGames[this.postGames.length - 1].id : null
      }
      this.$emit('preview', patch)
    },

    setPreviewPost(id) {
      this.$emit('preview', {postId: id})
    },

    onKeydown(e) {
      if (!this.active || e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return
      if (isTypingTarget(e.target) || document.body.classList.contains('modal-open')) return
      if (e.key === 'Enter') {
        // Enter на кнопке/ссылке — это её собственный клик
        const tag = (e.target?.tagName || '').toLowerCase()
        if (tag === 'button' || tag === 'a') return
        if (!this.canTake) return
        e.preventDefault()
        this.$emit('take')
        return
      }
      const scene = sceneByHotkey(e.key)
      if (scene) {
        e.preventDefault()
        this.setPreviewScene(scene)
      }
    },
  },
}
</script>

<style lang="scss">
.mb-air {
  display: flex;
  flex-direction: column;
  gap: 14px;

  &__scenes {
    display: grid;
    grid-template-columns: repeat(5, minmax(0, 1fr));
    gap: 8px;
  }

  &__scene {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
    padding: 9px 10px;
    border: 1px solid var(--tl-border);
    border-radius: 8px;
    background: var(--tl-bg);
    color: var(--tl-text);
    text-align: left;
    cursor: pointer;

    &:hover { border-color: var(--tl-primary); }
    &.is-preview { border-color: var(--tl-primary); box-shadow: 0 0 0 1px var(--tl-primary); }
    &.is-program .mb-air__key { background: var(--tl-danger); color: #fff; }

    .mb-badge { margin-left: auto; flex: none; }
  }

  &__key {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 30px;
    height: 30px;
    border-radius: 6px;
    background: var(--tl-soft);
    color: var(--tl-muted);
    font-weight: 700;
    font-size: 13px;
  }

  &__scene-text {
    display: flex;
    flex-direction: column;
    min-width: 0;

    b { font-size: 13px; font-weight: 600; }
    span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  }

  &__games {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__monitors {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(260px, 340px);
    gap: 16px;
    align-items: start;
  }

  &__side {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__monitor-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 6px;
    font-size: 13px;

    b { display: inline-flex; align-items: center; gap: 6px; }
  }

  &__take {
    width: 100%;
    justify-content: center;

    kbd {
      margin-left: 8px;
      padding: 1px 6px;
      border-radius: 4px;
      background: rgba(255, 255, 255, .2);
      color: inherit;
      font-size: 11px;
    }
  }

  &__note { margin: 0; line-height: 1.45; }

  &__obs {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  &__obs-row {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;

    code {
      flex: 1;
      min-width: 0;
      padding: 5px 8px;
      border-radius: 6px;
      background: var(--tl-soft);
      color: var(--tl-text);
    }

    .mb-btn { flex: none; }
  }

  @media (max-width: 1200px) {
    &__scenes { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    &__monitors { grid-template-columns: 1fr; }
  }
}
</style>
